import type { RequestConfig } from "@app/config/axios/http";
import { boApi, type ApiOmpBoState } from "@src/api/bo";
import { divisionApi, divisionServices, type ApiDivision } from "@src/api/dictionaries/divisions";
import type { PaginationReturns } from "@src/api/types/PaginationReturns";
import { wsDepotCardsServices } from "../wsDepotCards";
import type { WsDepotCard, WsDepotCardExtends } from "../wsDepotCards";
import { wsInvActsApi } from "./api.core";
import type {
    ApiWsInvAct,
    ApiWsInvActContent,
    WsInvAct,
    WsInvActContent,
    WsInvActContentExtends,
    WsInvActContentSetFactQtyData,
    WsInvActExtends,
    WsInvActFilter,
    ApiWsInvActContentsFilter,
} from "./types";

/**
 * Сервисный слой — АИ УП: обогащение, поиск по составу, установка фактического количества.
 */
export const wsInvActsServices = {
    ...wsInvActsApi,

    /**
     * Загружает акты инвентаризации и обогащает их связями подразделений и состояний БО.
     * Сервис сохраняет контракт `offset/limit` без изменения сигнатур.
     */
    load: async (
        params: {
            flt?: WsInvActFilter;
            include?: Array<keyof WsInvActExtends>;
            includeBo?: Array<"State">;
            offset?: number;
            limit?: number;
        },
        config?: RequestConfig,
    ): Promise<PaginationReturns<WsInvAct>> => {
        const { flt, include, includeBo = [], offset, limit } = params;

        const { items: invActs, totalQtyItems } = await wsInvActsApi.load({ flt, offset, limit }, config);

        if (include == null) {
            return {
                items: invActs.map((invAct) => serializeInvAct(invAct)),
                totalQtyItems,
            };
        }

        const divisionCodes = new Set<number>();
        const sectionCodes = new Set<number>();
        const boStateCodes = new Set<number>();

        const includeDivision = include.includes("Division");
        const includeDivisionSection = include.includes("DivisionSection");
        const includeBoData = include.includes("Bo");
        const includeBoState = includeBoData && includeBo?.includes("State");

        invActs.forEach((invAct) => {
            if (includeDivision) divisionCodes.add(invAct.DivisionCode);
            if (includeDivisionSection) sectionCodes.add(invAct.SectionCode);
            if (includeBoState) boStateCodes.add(invAct.Bo.State);
        });

        const divisions = new Map<number, ApiDivision>();
        const sections = new Map<number, ApiDivision>();
        const boStates = new Map<number, ApiOmpBoState>();

        const promises: Promise<unknown>[] = [];

        if (includeDivision && divisionCodes.size > 0) {
            const loadedDivisions = divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config);
            void loadedDivisions.then(({ items }) => items.forEach((div) => divisions.set(div.Code, div)));
            promises.push(loadedDivisions);
        }

        if (includeDivisionSection && sectionCodes.size > 0) {
            const loadedDivisionsSection = divisionServices.loadBySectionCodes({ sectionCodes: [...sectionCodes] }, config);
            void loadedDivisionsSection.then((items) =>
                items.forEach((item) => {
                    if (item.SectionCode !== 0) {
                        sections.set(item.SectionCode, item);
                    }
                }),
            );
            promises.push(loadedDivisionsSection);
        }

        if (includeBoState && boStateCodes.size > 0) {
            const loadedBoStates = boApi.states.load({ Codes: [...boStateCodes] }, config);
            void loadedBoStates.then((states) => states.forEach((state) => boStates.set(state.Code, state)));
            promises.push(loadedBoStates);
        }

        await Promise.all(promises);

        const items = invActs.map<WsInvAct>((invAct) =>
            serializeInvAct(invAct, {
                Division: includeDivision ? divisions.get(invAct.DivisionCode) : undefined,
                DivisionSection: includeDivisionSection ? sections.get(invAct.SectionCode ?? null) : undefined,
                Bo: {
                    StateCode: invAct.Bo.State,
                    State: includeBoState ? boStates.get(invAct.Bo.State) : undefined,
                },
            }),
        );

        return { items, totalQtyItems };
    },

    /** Возвращает один акт инвентаризации по фильтру. */
    loadOne: async (
        params: {
            flt: WsInvActFilter;
            include?: Array<keyof WsInvActExtends>;
            includeBo?: Array<"State">;
        },
        config?: RequestConfig,
    ): Promise<WsInvAct | null> => {
        const { items } = await wsInvActsServices.load(
            {
                ...params,
                limit: 1,
            },
            config,
        );

        return items[0] ?? null;
    },

    content: {
        ...wsInvActsApi.content,

        /** Загружает строки состава акта и обогащает карточкой УП по запросу `include`. */
        load: async (params: {
            flt?: ApiWsInvActContentsFilter;
            include?: Array<keyof WsInvActContentExtends>;
            includeWsDepotCard?: Array<keyof WsDepotCardExtends>;
            offset?: number;
            limit?: number;
        }): Promise<PaginationReturns<WsInvActContent>> => {
            const { flt, include, includeWsDepotCard, offset, limit } = params;

            const { items: contents, totalQtyItems } = await wsInvActsApi.content.load({ flt, offset, limit });

            if (include == null) {
                return {
                    items: contents.map((item) => serializeInvActContent(item)),
                    totalQtyItems,
                };
            }

            const wsCardCodes = new Set<number>();
            const _includeWsDepotCard = include.includes("WsDepotCard");

            contents.forEach((item) => {
                if (_includeWsDepotCard) wsCardCodes.add(item.WsDepotCardCode);
            });

            const wsDepotCards = new Map<number, WsDepotCard>();

            const promises: Promise<unknown>[] = [];

            if (_includeWsDepotCard && wsCardCodes.size > 0) {
                const cards = wsDepotCardsServices.load({
                    flt: { Codes: [...wsCardCodes] },
                    include: includeWsDepotCard,
                });
                void cards.then((cardItems) => cardItems.forEach((item: WsDepotCard) => wsDepotCards.set(item.Code, item)));
                promises.push(cards);
            }

            await Promise.all(promises);

            const items = contents.map<WsInvActContent>((item) =>
                serializeInvActContent(item, {
                    WsDepotCard: _includeWsDepotCard ? wsDepotCards.get(item.WsDepotCardCode) : undefined,
                }),
            );

            return { items, totalQtyItems };
        },

        /** Возвращает одну строку состава акта по фильтру. */
        loadOne: async (params: {
            flt: ApiWsInvActContentsFilter;
            include?: Array<keyof WsInvActContentExtends>;
            includeWsDepotCard?: Array<keyof WsDepotCardExtends>;
        }): Promise<WsInvActContent | null> => {
            const { items } = await wsInvActsServices.content.load({
                ...params,
                limit: 1,
            });

            return items[0] ?? null;
        },

        /** Устанавливает фактическое количество с расчетом дельты относительно исходного значения. */
        setFactQty: async (params: { data: WsInvActContentSetFactQtyData }): Promise<number> => {
            const { contentCode, initialFactQty, factQty } = params.data;

            const { items } = await wsInvActsServices.content.load({
                flt: { Codes: [contentCode] },
            });
            const currentContentData = items[0];

            const delta = factQty - initialFactQty;

            await wsInvActsApi.content.setFactQty({
                data: {
                    ContentCode: contentCode,
                    FactQty: currentContentData.FactQty + delta,
                },
            });

            return currentContentData.FactQty + delta;
        },
    },
} as const;

function serializeInvAct(apiInvAct: ApiWsInvAct, includeData?: Partial<WsInvActExtends>): WsInvAct {
    return {
        Code: apiInvAct.Code,
        DivisionCode: apiInvAct.DivisionCode,
        Division: includeData?.Division,
        SectionCode: apiInvAct.SectionCode,
        DivisionSection: includeData?.DivisionSection,
        CommandNum: apiInvAct.CommandNum,
        CommandDate: apiInvAct.CommandDate,
        BeginDate: apiInvAct.BeginDate,
        MovesDate: apiInvAct.MovesDate,
        MatRespLabCode1: apiInvAct.MatRespLabCode1,
        MatRespLabCode2: apiInvAct.MatRespLabCode2,
        Bo: includeData?.Bo,
    };
}

function serializeInvActContent(
    apiData: ApiWsInvActContent,
    includeData?: Partial<WsInvActContentExtends>,
): WsInvActContent {
    return {
        ...apiData,
        WsDepotCard: includeData?.WsDepotCard,
    };
}
